import type { JSX } from 'react';
import type {
  ContactResource,
  CreateDealBody,
  DealDetailResource,
  DealModelValue,
  OpenStageValue,
  ReminderTypeValue,
  UpdateDealBody,
} from '@majuscule/contracts';
import { openStageSchema, reminderTypeSchema } from '@majuscule/contracts';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import {
  addDays,
  crmSend,
  errorMessage,
  euroInputError,
  euroInputOf,
  MODEL_HINT,
  MODEL_LABEL,
  parisToday,
  parseEuroInput,
  REMINDER_TYPE_LABEL,
  REMINDER_TYPES,
  ROLE_LABEL,
  STAGE_LABEL,
  STAGE_NEXT_SUBJECT,
  STAGE_PROBABILITY,
  stagesForModel,
} from '../../lib/crm';
import { Choices, MutationError, useCompanies, useCompany, useCrmRefresh } from './shared';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHead,
} from '@/components/ui/dialog';
import { CheckRow, Field, FieldHint, Fields2, Fieldset, InputGroup } from '@/components/ui/field';
import { DateInput, TimeInput } from '@/components/ui/date-input';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';

const MODELS: readonly DealModelValue[] = ['custom', 'shared_license'];

function contactHint(contact: ContactResource): string {
  return [contact.jobTitle, ...contact.roles.map((role) => ROLE_LABEL[role].toLowerCase())]
    .filter((part): part is string => part !== null && part !== '')
    .join(', ');
}

/** Contacts of a company as a check list (deal contacts). */
function ContactChecks({
  contacts,
  selected,
  onChange,
}: {
  contacts: readonly ContactResource[];
  selected: readonly string[];
  onChange: (next: string[]) => void;
}): JSX.Element {
  if (contacts.length === 0) {
    return <FieldHint>Aucun contact dans cette entreprise.</FieldHint>;
  }
  return (
    <div className="grid gap-2">
      {contacts.map((contact) => (
        <CheckRow
          key={contact.id}
          label={
            <>
              {contact.firstName} {contact.lastName}{' '}
              <small className="text-xs text-muted-foreground">{contactHint(contact)}</small>
            </>
          }
          checked={selected.includes(contact.id)}
          onChange={(event) => {
            onChange(
              event.target.checked
                ? [...selected, contact.id]
                : selected.filter((id) => id !== contact.id),
            );
          }}
        />
      ))}
    </div>
  );
}

function probabilityError(value: string): string | undefined {
  const n = Number(value);
  return value.trim() === '' || !Number.isInteger(n) || n < 0 || n > 100
    ? 'Probabilité entière entre 0 et 100.'
    : undefined;
}

/** « Nouvelle affaire » (DealDialog.dc.html). */
export function CreateDealDialog({
  open,
  companyId,
  onClose,
}: {
  open: boolean;
  /** Preselected company (company page); null: picked in the dialog. */
  companyId: string | null;
  onClose: () => void;
}): JSX.Element {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
    >
      {open ? <CreateDealForm initialCompanyId={companyId} onClose={onClose} /> : null}
    </Dialog>
  );
}

function CreateDealForm({
  initialCompanyId,
  onClose,
}: {
  initialCompanyId: string | null;
  onClose: () => void;
}): JSX.Element {
  const refresh = useCrmRefresh();
  const navigate = useNavigate();
  const [companyId, setCompanyId] = useState(initialCompanyId ?? '');
  const [title, setTitle] = useState('');
  const [model, setModel] = useState<DealModelValue>('custom');
  const [stage, setStage] = useState<OpenStageValue>('to_qualify');
  const [closeDate, setCloseDate] = useState('');
  const [setup, setSetup] = useState('');
  const [monthly, setMonthly] = useState('');
  const [probability, setProbability] = useState(String(STAGE_PROBABILITY.to_qualify));
  const [contactIds, setContactIds] = useState<string[]>([]);
  const [subject, setSubject] = useState(STAGE_NEXT_SUBJECT.to_qualify);
  const [nextType, setNextType] = useState<ReminderTypeValue>('call');
  const [nextDate, setNextDate] = useState(addDays(parisToday(), 1));
  const [nextTime, setNextTime] = useState('09:00');
  const [submitted, setSubmitted] = useState(false);

  const companies = useCompanies(initialCompanyId === null);
  const company = useCompany(companyId === '' ? null : companyId);

  const changeStage = (next: OpenStageValue): void => {
    // Defaults follow the stage unless the operator changed them.
    if (probability === String(STAGE_PROBABILITY[stage])) {
      setProbability(String(STAGE_PROBABILITY[next]));
    }
    if (subject === STAGE_NEXT_SUBJECT[stage]) {
      setSubject(STAGE_NEXT_SUBJECT[next]);
    }
    setStage(next);
  };

  const changeModel = (next: DealModelValue): void => {
    setModel(next);
    if (!stagesForModel(next).includes(stage)) {
      changeStage('signature_pending');
    }
  };

  const errors = {
    company: companyId === '' ? 'Choisir une entreprise.' : undefined,
    title: title.trim() === '' ? 'Indiquer l’intitulé de la brique.' : undefined,
    setup: euroInputError(setup),
    monthly: euroInputError(monthly),
    probability: probabilityError(probability),
    subject: subject.trim() === '' ? 'Indiquer la prochaine action.' : undefined,
    nextDate: nextDate === '' ? 'Choisir une date.' : undefined,
    nextTime: nextTime === '' ? 'Choisir une heure.' : undefined,
  };
  const valid = Object.values(errors).every((error) => error === undefined);
  const shown = (error: string | undefined): string | undefined => (submitted ? error : undefined);

  const create = useMutation({
    mutationFn: () => {
      const body: CreateDealBody = {
        companyId,
        title: title.trim(),
        model,
        stage,
        probability: Number(probability),
        setup: parseEuroInput(setup) ?? null,
        monthly: parseEuroInput(monthly) ?? null,
        expectedCloseDate: closeDate === '' ? null : closeDate,
        contactIds,
        nextAction: {
          subject: subject.trim(),
          type: nextType,
          dueDate: nextDate,
          dueTime: nextTime,
        },
      };
      return crmSend<DealDetailResource>('POST', '/deals', body);
    },
    onSuccess: (detail) => {
      refresh();
      onClose();
      void navigate({ to: '/deals/$dealId', params: { dealId: detail.deal.id } });
    },
  });

  return (
    <DialogContent width={600}>
      <DialogHead
        title="Nouvelle affaire"
        description="Une affaire correspond à une brique vendue."
      />
      <form
        id="deal-form"
        className="contents"
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitted(true);
          if (valid) {
            create.mutate();
          }
        }}
      >
        <DialogBody>
          <Fieldset title="Affaire">
            {initialCompanyId === null ? (
              <Field label="Entreprise" required error={shown(errors.company)}>
                <NativeSelect
                  value={companyId}
                  onChange={(event) => {
                    setCompanyId(event.target.value);
                    setContactIds([]);
                  }}
                >
                  <option value="">Choisir une entreprise</option>
                  {(companies.data?.companies ?? []).map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
            ) : null}
            <Field label="Intitulé de la brique" required error={shown(errors.title)}>
              <Input
                value={title}
                maxLength={200}
                placeholder="Brique 3 : planning des équipes"
                onChange={(event) => {
                  setTitle(event.target.value);
                }}
              />
            </Field>
            <div className="grid gap-1.5">
              <Label id="deal-model-label">
                Modèle<span aria-hidden="true"> *</span>
              </Label>
              <Choices<DealModelValue>
                labelledBy="deal-model-label"
                value={model}
                onChange={changeModel}
                items={MODELS.map((value) => ({
                  value,
                  label: MODEL_LABEL[value],
                  hint: MODEL_HINT[value],
                }))}
              />
            </div>
            <Fields2>
              <Field label="Étape">
                <NativeSelect
                  value={stage}
                  onChange={(event) => {
                    const parsed = openStageSchema.safeParse(event.target.value);
                    if (parsed.success) {
                      changeStage(parsed.data);
                    }
                  }}
                >
                  {stagesForModel(model).map((value) => (
                    <option key={value} value={value}>
                      {STAGE_LABEL[value]}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field label="Signature prévue">
                <DateInput
                  value={closeDate}
                  onChange={(value) => {
                    setCloseDate(value);
                  }}
                />
              </Field>
            </Fields2>
          </Fieldset>

          <Fieldset title="Montants HT">
            <Fields2>
              <Field label="Signature (mise en place)" error={shown(errors.setup)}>
                <InputGroup suffix="€">
                  <Input
                    numeric
                    inputMode="decimal"
                    placeholder="À chiffrer"
                    value={setup}
                    onChange={(event) => {
                      setSetup(event.target.value);
                    }}
                  />
                </InputGroup>
              </Field>
              <Field label="Hébergement mensuel" error={shown(errors.monthly)}>
                <InputGroup suffix="€">
                  <Input
                    numeric
                    inputMode="decimal"
                    placeholder="À chiffrer"
                    value={monthly}
                    onChange={(event) => {
                      setMonthly(event.target.value);
                    }}
                  />
                </InputGroup>
              </Field>
            </Fields2>
            <Field
              label="Probabilité"
              error={shown(errors.probability)}
              hint={`Valeur par défaut de l’étape ${STAGE_LABEL[stage]} : ${STAGE_PROBABILITY[stage]} %. Le montant pondéré vaut mise en place plus 12 mois d’abonnement, multiplié par la probabilité.`}
            >
              <InputGroup suffix="%" className="max-w-40">
                <Input
                  numeric
                  inputMode="numeric"
                  value={probability}
                  onChange={(event) => {
                    setProbability(event.target.value);
                  }}
                />
              </InputGroup>
            </Field>
          </Fieldset>

          {companyId !== '' ? (
            <Fieldset title="Contacts liés">
              <ContactChecks
                contacts={company.data?.contacts ?? []}
                selected={contactIds}
                onChange={setContactIds}
              />
            </Fieldset>
          ) : null}

          <Fieldset title="Prochaine action *">
            <Field label="Objet" error={shown(errors.subject)}>
              <Input
                value={subject}
                maxLength={200}
                onChange={(event) => {
                  setSubject(event.target.value);
                }}
              />
            </Field>
            <Fields2>
              <Field label="Date" error={shown(errors.nextDate)}>
                <DateInput
                  value={nextDate}
                  onChange={(value) => {
                    setNextDate(value);
                  }}
                />
              </Field>
              <Field label="Heure" error={shown(errors.nextTime)}>
                <TimeInput
                  value={nextTime}
                  onChange={(value) => {
                    setNextTime(value);
                  }}
                />
              </Field>
              <Field label="Type">
                <NativeSelect
                  value={nextType}
                  onChange={(event) => {
                    const parsed = reminderTypeSchema.safeParse(event.target.value);
                    if (parsed.success) {
                      setNextType(parsed.data);
                    }
                  }}
                >
                  {REMINDER_TYPES.map((value) => (
                    <option key={value} value={value}>
                      {REMINDER_TYPE_LABEL[value]}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
            </Fields2>
            <FieldHint>
              Une affaire ouverte a toujours une prochaine action. Sans elle, l’affaire est signalée
              dans le pipeline et dans À faire.
            </FieldHint>
          </Fieldset>
          <MutationError
            message={create.isError ? errorMessage(create.error, 'Échec de la création.') : null}
          />
        </DialogBody>
      </form>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Annuler
        </Button>
        <Button type="submit" form="deal-form" size="lg" disabled={create.isPending}>
          Créer l’affaire
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

/** « Modifier l'affaire »: title, amounts, probability, expected signature, contacts. */
export function EditDealDialog({
  detail,
  onClose,
}: {
  detail: DealDetailResource | null;
  onClose: () => void;
}): JSX.Element {
  return (
    <Dialog
      open={detail !== null}
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
    >
      {detail !== null ? <EditDealForm detail={detail} onClose={onClose} /> : null}
    </Dialog>
  );
}

function EditDealForm({
  detail,
  onClose,
}: {
  detail: DealDetailResource;
  onClose: () => void;
}): JSX.Element {
  const refresh = useCrmRefresh();
  const { deal } = detail;
  const [title, setTitle] = useState(deal.title);
  const [setup, setSetup] = useState(euroInputOf(deal.setup));
  const [monthly, setMonthly] = useState(euroInputOf(deal.monthly));
  const [aiMonthly, setAiMonthly] = useState(euroInputOf(deal.aiMonthly));
  const [probability, setProbability] = useState(String(deal.probability));
  const [closeDate, setCloseDate] = useState(deal.expectedCloseDate ?? '');
  const [contactIds, setContactIds] = useState<string[]>(detail.contacts.map((c) => c.id));
  const [submitted, setSubmitted] = useState(false);

  const errors = {
    title: title.trim() === '' ? 'Indiquer l’intitulé de la brique.' : undefined,
    setup: euroInputError(setup),
    monthly: euroInputError(monthly),
    aiMonthly: euroInputError(aiMonthly),
    probability: probabilityError(probability),
  };
  const valid = Object.values(errors).every((error) => error === undefined);
  const shown = (error: string | undefined): string | undefined => (submitted ? error : undefined);

  const save = useMutation({
    mutationFn: () => {
      const body: UpdateDealBody = {
        aiMonthly: parseEuroInput(aiMonthly) ?? null,
        title: title.trim(),
        setup: parseEuroInput(setup) ?? null,
        monthly: parseEuroInput(monthly) ?? null,
        probability: Number(probability),
        expectedCloseDate: closeDate === '' ? null : closeDate,
        contactIds,
      };
      return crmSend<DealDetailResource>('PATCH', `/deals/${deal.id}`, body);
    },
    onSuccess: () => {
      refresh();
      onClose();
    },
  });

  return (
    <DialogContent width={600} aria-describedby={undefined}>
      <DialogHead title="Modifier l’affaire" description={detail.company.name} />
      <form
        id="deal-edit-form"
        className="contents"
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitted(true);
          if (valid) {
            save.mutate();
          }
        }}
      >
        <DialogBody>
          <Field label="Intitulé de la brique" required error={shown(errors.title)}>
            <Input
              value={title}
              maxLength={200}
              onChange={(event) => {
                setTitle(event.target.value);
              }}
            />
          </Field>
          <Fieldset title="Montants HT">
            <Fields2>
              <Field label="Signature (mise en place)" error={shown(errors.setup)}>
                <InputGroup suffix="€">
                  <Input
                    numeric
                    inputMode="decimal"
                    placeholder="À chiffrer"
                    value={setup}
                    onChange={(event) => {
                      setSetup(event.target.value);
                    }}
                  />
                </InputGroup>
              </Field>
              <Field label="Hébergement mensuel" error={shown(errors.monthly)}>
                <InputGroup suffix="€">
                  <Input
                    numeric
                    inputMode="decimal"
                    placeholder="À chiffrer"
                    value={monthly}
                    onChange={(event) => {
                      setMonthly(event.target.value);
                    }}
                  />
                </InputGroup>
              </Field>
            </Fields2>
            <Field label="Frais d’IA estimés (par mois)" error={shown(errors.aiMonthly)}>
              <InputGroup suffix="€">
                <Input
                  numeric
                  inputMode="decimal"
                  placeholder="Facturés à la consommation"
                  value={aiMonthly}
                  onChange={(event) => {
                    setAiMonthly(event.target.value);
                  }}
                />
              </InputGroup>
            </Field>
            <Fields2>
              <Field label="Probabilité" error={shown(errors.probability)}>
                <InputGroup suffix="%">
                  <Input
                    numeric
                    inputMode="numeric"
                    value={probability}
                    onChange={(event) => {
                      setProbability(event.target.value);
                    }}
                  />
                </InputGroup>
              </Field>
              <Field label="Signature prévue">
                <DateInput
                  value={closeDate}
                  onChange={(value) => {
                    setCloseDate(value);
                  }}
                />
              </Field>
            </Fields2>
          </Fieldset>
          <Fieldset title="Contacts liés">
            <ContactChecks
              contacts={detail.companyContacts}
              selected={contactIds}
              onChange={setContactIds}
            />
          </Fieldset>
          <MutationError
            message={save.isError ? errorMessage(save.error, 'Échec de l’enregistrement.') : null}
          />
        </DialogBody>
      </form>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Annuler
        </Button>
        <Button type="submit" form="deal-edit-form" size="lg" disabled={save.isPending}>
          Enregistrer
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
